import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Slide, List, Typography } from '@mui/material';
import logo from '../../../assets/images/logo-app.png';
import xIcon from '../../../assets/fonts/close.svg';
import { routes, adminRoutes } from '../../../constants/sideBarRoutes';
import { NavLink } from 'react-router-dom';
import { CustomSideTab } from '../SideBar/CustomSideTab/CustomSideTab';
import { generalActions } from '../../../backend/store/reducers/generalReducer';
import { useTranslation } from "../../../hooks/useTranslation";
import { UserContext } from "../../../contexts/UserContext";
import { canAccessAny, hasPermission } from "../../../utils/permissions";
import { showsVolunteeringFor } from "../../../utils/showsVolunteering";
import { billingTriggerActions } from "../../../backend/store/reducers/billingReducer";
import { SelectWorkspace } from "../SelectWorkspace/index";
import ExportDataModal from "../../Onboarding/ExportDataModal";
import ContactUsModal from "../ContactUsModal/ContactUsModal";
import { ImpersonationSidePanel } from "../Impersonation";
import TutorialsModal from "../TutorialsModal/TutorialsModal";
import { LegalDocumentsButton } from "../LegalDocuments/LegalDocumentsButton";
import { isLegacyView, switchLegacyView } from "../../../next/preview/legacyView";

export const SideBarMobile = () => {
    const { p } = useTranslation('sidebar');
    const dispatch = useDispatch();
    const isVisible = useSelector((state: any) => state.generalStore.visibleSideBar);
    const storedUser = useSelector((state: any) => state.authStore.currentUser);
    const currentUser = useMemo(() => storedUser, [storedUser]);

    const billingStore = useSelector((state: any) => state.billingStore);
    const hasActiveSubscription = billingStore.hasActiveSubscription;
    const isTrialActive = billingStore.isTrialActive;
    const contactCount = billingStore.contactCount;
    const isPreTrial = billingStore.trialEndsAt === null
        && !billingStore.hasActiveSubscription
        && !billingStore.isSuperAdminWorkspace;

    const [valueSelect, setHandleSelect] = useState("");
    const [workspacesUser, setWorkspacesUser] = useState([]);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);
    const { user } = useContext(UserContext);
    const isWorkspaceLocked = localStorage.getItem("workspace_locked") === "true";


    useEffect(() => {
        // Only people with billing rights may read the subscription and usage (the others got a 403 on every page).
        if (currentUser && hasPermission("read_billing", currentUser)) {
            dispatch(billingTriggerActions.getSubscription());
            dispatch(billingTriggerActions.getUsage());
        }
    }, [currentUser, dispatch]);

    useEffect(() => {
        if (!currentUser) return;
        setWorkspacesUser(
            currentUser.workspaces.map((workspace: { name: string; id: number }) => ({
                name: workspace.name,
                id: workspace.id,
            }))
        );

        let myValue: string = "";
        currentUser.workspaces.forEach((element: any) => {
            if (element.id === currentUser.currentWorkspace) {
                myValue = element.name;
                return;
            }
        });
        setHandleSelect(myValue);
    }, [currentUser]);


    function normalizeScopeType(scopeType?: string): 'routes' | 'households' | undefined {
        if (scopeType === 'routes' || scopeType === 'households') return scopeType;
        return undefined;
    }

    const closeSideBar = () => {
        dispatch(generalActions.setVisibleSideBar(false));
    };

    return (
        <>
            {isVisible && (
                <Slide in={isVisible} direction={'right'}>
                    <Box
                        sx={{
                            width: '80%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            zIndex: 2000,
                            backgroundColor: '#FFFFFF',
                            padding: '10px 20px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>

                        <Box sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexShrink: 0,
                            mb: 2
                        }}>
                            <img src={logo} alt="logo" style={{ height: 57, width: "auto" }} />
                            <img 
                                onClick={closeSideBar}
                                src={xIcon}
                                alt="" 
                                style={{ cursor: 'pointer' }}
                            />
                        </Box>

                        <ImpersonationSidePanel />

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                            {/* <Typography sx={{
                                color: '#6A6A99',
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                opacity: 0.4,
                                paddingLeft: '6px'
                            }}>{p('general')}</Typography> */}
                            
                            <List className="sidebar__ul-general">
                                {routes
                                    .filter((item: any) => item.group === "general")
                                    .map((element: any, index: number) => {
                                        const isSuperAdmin = currentUser?.role === "super_admin";
                                        const isBillingPath = element.link === "/billing";
                                        const isExportItem = element.modal === "export";

                                        if (!isSuperAdmin && !hasActiveSubscription && !isTrialActive && !isPreTrial) {
                                            if (isExportItem) {
                                                if (!(contactCount > 0)) return null;
                                            } else if (!isBillingPath) {
                                                return null;
                                            }
                                        } else if (isExportItem) {
                                            return null;
                                        }

                                        // Scoped-resource entries use the scope-presence check; scope-less
                                        // page permissions (e.g. the Map page's read_map) are matched against
                                        // general + scoped.permissions via hasPermission.
                                        if (element.permission) {
                                            const scope = normalizeScopeType(element.scopeType);
                                            const allowed = scope
                                                ? canAccessAny(user, element.permission, scope)
                                                : hasPermission(element.permission, user);
                                            if (!allowed) {
                                                return null;
                                            }
                                        }

                                        // Task 12: see the matching comment in SideBar.tsx --
                                        // the volunteering entry needs one more condition
                                        // beyond holding the permission (showsVolunteering.ts).
                                        if (element.requiresVolunteerConnection) {
                                            // Whole-branch review fix (MINOR 6):
                                            // see the matching comment in SideBar.tsx --
                                            // showsVolunteeringFor() replaces the
                                            // hand-copied permission flattening.
                                            if (!showsVolunteeringFor(user)) {
                                                return null;
                                            }
                                        }

                                        if (isExportItem) {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        setIsExportModalOpen(true);
                                                        closeSideBar();
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={false}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (element.modal === "tutorials") {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        setIsTutorialsModalOpen(true);
                                                        closeSideBar();
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={false}
                                                    />
                                                </div>
                                            );
                                        }

                                        return (
                                            <NavLink
                                                to={element.link}
                                                key={index}
                                                style={{ textDecoration: "none" }}
                                                onClick={closeSideBar}
                                            >
                                                {({ isActive }) => (
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={isActive}
                                                    />
                                                )}
                                            </NavLink>
                                        );
                                    })
                                }
                            </List>

                            {/* <Typography sx={{
                                color: '#6A6A99',
                                fontSize: '11px',
                                marginTop: 3,
                                textTransform: 'uppercase',
                                opacity: 0.4,
                                paddingLeft: '14px'
                            }}>{p('about_us')}</Typography> */}

                            <List className="sidebar__ul-about">
                                {routes
                                    .filter((item: any) => item.group === "about")
                                    .map((element: any, index: number) => {
                                        if (element.modal === "contact_us") {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        setIsContactModalOpen(true);
                                                        closeSideBar();
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={false}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (element.modal === "tutorials") {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        setIsTutorialsModalOpen(true);
                                                        closeSideBar();
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={false}
                                                    />
                                                </div>
                                            );
                                        }

                                        return (
                                            <NavLink
                                                to={element.link}
                                                key={index}
                                                style={{ textDecoration: "none" }}
                                                onClick={closeSideBar}
                                            >
                                                {({ isActive }) => (
                                                    <CustomSideTab
                                                        icon={element.icon}
                                                        text={element.route}
                                                        isActive={isActive}
                                                    />
                                                )}
                                            </NavLink>
                                        );
                                    })}
                            </List>

                            {(() => {
                                const isSuperAdmin = currentUser?.role === "super_admin";
                                if (!isSuperAdmin && !hasActiveSubscription && !isTrialActive && !isPreTrial) {
                                    return null;
                                }

                                const visibleAdminRoutes = adminRoutes.filter(route =>
                                    hasPermission(route.permission, currentUser)
                                );

                                return visibleAdminRoutes.length > 0 && (
                                    <>
                                        <Typography sx={{
                                            color: '#6A6A99',
                                            fontSize: '11px',
                                            marginTop: 3,
                                            textTransform: 'uppercase',
                                            opacity: 0.4,
                                            paddingLeft: '6px'
                                        }}>
                                            {p('admin') || 'Admin'}
                                        </Typography>
                                        <List className="sidebar__ul-general">
                                            {/* "Show me the old version" is on (staff, saved on the server): the way back to the new design. */}
                                            {isSuperAdmin && !currentUser?.is_impersonating && isLegacyView() && (
                                                <CustomSideTab
                                                    icon={visibleAdminRoutes[0].icon}
                                                    text="back_to_new_design"
                                                    isActive={false}
                                                    onClick={() => { void switchLegacyView(false).catch(() => undefined); }}
                                                />
                                            )}
                                            {visibleAdminRoutes.map((element, index) => (
                                                <NavLink
                                                    to={element.link}
                                                    key={`admin-${index}`}
                                                    style={{ textDecoration: "none" }}
                                                    onClick={closeSideBar}
                                                >
                                                    {({ isActive }) => (
                                                        <CustomSideTab
                                                            icon={element.icon}
                                                            text={element.route}
                                                            isActive={isActive}
                                                        />
                                                    )}
                                                </NavLink>
                                            ))}
                                        </List>
                                    </>
                                );
                            })()}
                        </Box>

                        <Box sx={{ mt: 'auto', pt: 2, position: 'relative', zIndex: 2001 }}>
                            {!isWorkspaceLocked && (
                                <Box sx={{ py: 2 }}>
                                    <SelectWorkspace
                                        label={""}
                                        list={workspacesUser}
                                        value={valueSelect}
                                        placeholder={"Workspace name"}
                                        hasMultipleWorkspaces={
                                            currentUser?.hasMultipleWorkspaces ||
                                            workspacesUser.length > 1 ||
                                            false
                                        }
                                        handleSelect={setHandleSelect}
                                        onChangeClose
                                    />
                                </Box>
                            )}
                            <LegalDocumentsButton variant="sidebar" />

                            {/* <Box sx={{
                                paddingBottom: '20px',
                                paddingLeft: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '25px',
                                cursor: 'pointer'
                            }}
                                onClick={logout}
                            >
                                <PowerSettingsNewIcon sx={{ color: '#6A6A99' }} />
                                <Typography sx={{
                                    fontFamily: 'Rubik, sans-serif',
                                    letterSpacing: '0.2px',
                                    opacity: '0.6',
                                    fontWeight: '400'
                                }}>
                                    {pg('logout') || 'Log Out'}
                                </Typography>
                            </Box> */}
                        </Box>


                    </Box>
                </Slide >
            )}
            <ExportDataModal
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
            />
            <ContactUsModal
                open={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
            />
            <TutorialsModal
                open={isTutorialsModalOpen}
                onClose={() => setIsTutorialsModalOpen(false)}
            />
        </>
    );
};
